import React, { useState, useEffect, useRef } from 'react';
import AudioGenerator from '../utils/audioUtils';

const MUSIC_SRC = process.env.PUBLIC_URL + '/bensound-buddy.mp3';
const WIN_SRC = process.env.PUBLIC_URL + '/small-crowd-clapping.mp3';

const SoundManager = ({ children }) => {
  const [isMusicOn, setIsMusicOn] = useState(true);
  const [isSoundOn, setIsSoundOn] = useState(true);
  const [volume, setVolume] = useState(0.7);
  
  const audioGeneratorRef = useRef(null);
  const musicAudioRef = useRef(null);
  const winAudioRef = useRef(null);

  // Initialize audio generator
  useEffect(() => {
    audioGeneratorRef.current = new AudioGenerator();
  }, []);

  // Play/pause music when toggled
  useEffect(() => {
    if (musicAudioRef.current) {
      musicAudioRef.current.volume = volume * 0.3;
      if (isMusicOn) {
        musicAudioRef.current.play().catch(() => {});
      } else {
        musicAudioRef.current.pause();
      }
    }
  }, [isMusicOn, volume]);

  // Set win sound volume
  useEffect(() => {
    if (winAudioRef.current) {
      winAudioRef.current.volume = volume;
    }
  }, [volume]);

  // Resume AudioContext and music on user interaction
  const resumeAudio = () => {
    if (audioGeneratorRef.current && audioGeneratorRef.current.audioContext && audioGeneratorRef.current.audioContext.state === 'suspended') {
      audioGeneratorRef.current.audioContext.resume();
    }
    if (musicAudioRef.current) {
      musicAudioRef.current.play().catch(() => {});
    }
  };

  // Toggle background music
  const toggleMusic = () => {
    setIsMusicOn((prev) => !prev);
  };

  // Toggle sound effects
  const toggleSound = () => {
    setIsSoundOn(!isSoundOn);
  };

  // Sound effect functions
  const playDiceSound = () => {
    if (isSoundOn && audioGeneratorRef.current) {
      audioGeneratorRef.current.generateDiceSound();
    }
  };

  const playTurnChangeSound = () => {
    if (isSoundOn && audioGeneratorRef.current) {
      audioGeneratorRef.current.generateTurnChangeSound();
    }
  };

  const playNotificationSound = () => {
    if (isSoundOn && audioGeneratorRef.current) {
      audioGeneratorRef.current.generateNotificationSound();
    }
  };

  const playCorrectAnswerSound = () => {
    if (isSoundOn && audioGeneratorRef.current) {
      audioGeneratorRef.current.generateCorrectAnswerSound();
    }
  };

  const playWrongAnswerSound = () => {
    if (isSoundOn && audioGeneratorRef.current) {
      audioGeneratorRef.current.generateWrongAnswerSound();
    }
  };

  const playGameWinSound = () => {
    if (isSoundOn && winAudioRef.current) {
      winAudioRef.current.currentTime = 0;
      winAudioRef.current.play().catch(() => {});
    }
  };

  // Context value to pass to children
  const soundContext = {
    playDiceSound,
    playTurnChangeSound,
    playNotificationSound,
    playCorrectAnswerSound,
    playWrongAnswerSound,
    playGameWinSound,
    isMusicOn,
    isSoundOn,
    volume,
    setVolume,
    resumeAudio
  };

  return (
    <div className="relative">
      {/* Hidden audio element for background music */}
      <audio
        ref={musicAudioRef}
        src={MUSIC_SRC}
        loop
        style={{ display: 'none' }}
        preload="auto"
      />
      {/* Hidden audio element for win sound */}
      <audio
        ref={winAudioRef}
        src={WIN_SRC}
        style={{ display: 'none' }}
        preload="auto"
      />
      {/* Audio Controls */}
      <div className="fixed top-4 right-4 z-50 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-4 border border-gray-200">
        <div className="flex flex-col gap-3">
          {/* Music Toggle */}
          <button
            onClick={toggleMusic}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
              isMusicOn 
                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            title={isMusicOn ? 'Turn off music' : 'Turn on music'}
          >
            <span className="text-lg">
              {isMusicOn ? '🎵' : '🔇'}
            </span>
            <span className="text-sm font-medium">Music</span>
          </button>

          {/* Sound Effects Toggle */}
          <button
            onClick={toggleSound}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
              isSoundOn 
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            title={isSoundOn ? 'Turn off sound effects' : 'Turn on sound effects'}
          >
            <span className="text-lg">
              {isSoundOn ? '🔊' : '🔇'}
            </span>
            <span className="text-sm font-medium">Sounds</span>
          </button>

          {/* Volume Slider */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">🔊</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              title="Volume"
            />
          </div>
        </div>
      </div>

      {/* Render children with sound context */}
      {children(soundContext)}
    </div>
  );
};

export default SoundManager; 